import { createContext, useContext } from "react";
import type { SrcSchemasChatMessageResponse } from "@/api";
import type { ChatHost } from "../chatHost";
import { isContinuation } from "../PausedNotice";
import type { SendInput } from "../useSendMessage";

/** What a chat lets its saved replies do beyond Copy (Figma "Action row"). Absent: a reply can only be copied. */
export interface ReplyActionsValue {
  /** The chat, for links to its messages. */
  sessionUuid: string;
  host: ChatHost;
  /** The chat's space: a reply can be saved to its knowledge. */
  spaceUuid?: string | null;
  /** Try again on the chat's latest reply (the only one it applies to); unset when it can't be sent now. */
  tryAgain?: { replyUuid: string; run: () => void };
}

const ReplyActionsContext = createContext<ReplyActionsValue | null>(null);
export const ReplyActionsProvider = ReplyActionsContext.Provider;
export const useReplyActionsContext = () => useContext(ReplyActionsContext);

type Send = Pick<SendInput, "agentUuid" | "modelId" | "reasoningEffort">;

/**
 * Try again for the chat's latest reply: its question is sent again with the reply's operation as `retryOf`, so the
 * backend replaces the reply, and the turn hides it from the question on (`replaceFrom`). Not for a reply to a
 * Continue (its question is the step limit's), nor one saved before replies recorded their operation.
 */
export function tryAgainFor(
  messages: readonly SrcSchemasChatMessageResponse[],
  start: (input: Omit<SendInput, "sessionUuid">, onFailed: () => void, files: [], replaceFrom: number) => void,
  send: Send,
): ReplyActionsValue["tryAgain"] {
  const reply = messages.at(-1);
  const operationId = reply?.messageMetadata?.operation_id;
  if (reply?.role !== "assistant" || typeof operationId !== "string") return undefined;
  const askedAt = messages.findLastIndex((m) => m.role === "user");
  const asked = messages[askedAt];
  if (!asked || isContinuation(asked)) return undefined;
  return {
    replyUuid: reply.uuid,
    run: () => start({ ...send, content: asked.content, retryOf: operationId }, () => {}, [], askedAt + 1),
  };
}

/**
 * What a chat's saved replies can do: none before the chat exists; Try again only for its owner, and not while a
 * reply is being written.
 */
export function replyActionsFor(
  chat: { sessionUuid?: string; host: ChatHost; spaceUuid?: string | null; isOwner: boolean; busy: boolean },
  messages: readonly SrcSchemasChatMessageResponse[],
  start: Parameters<typeof tryAgainFor>[1],
  send: Send,
): ReplyActionsValue | null {
  if (!chat.sessionUuid) return null;
  return {
    sessionUuid: chat.sessionUuid,
    host: chat.host,
    spaceUuid: chat.spaceUuid,
    tryAgain: chat.isOwner && !chat.busy ? tryAgainFor(messages, start, send) : undefined,
  };
}
